import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Calendar, Clock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
import { sendBokabordMail } from '../utils/infoMail';
interface BookTableModalProps {
  onClose: () => void;
}
type Step = 'meal-selection' | 'booking-form' | 'success';
type MealType = 'Frukost' | 'Fika' | 'Lunch' | 'AW';
const mealOptions: {
  type: MealType;
  description: string;
}[] = [{
  type: 'Frukost',
  description: 'Starta dagen på bästa sätt med nybakat.'
}, {
  type: 'Fika',
  description: 'Kaffe och något sött från vårt hantverksbageri.'
}, {
  type: 'Lunch',
  description: 'Vällagad mat från grunden.'
}, {
  type: 'AW',
  description: 'Avsluta dagen med god dryck och tilltugg.'
}];
export function BookTableModal({
  onClose
}: BookTableModalProps) {
  const [step, setStep] = useState<Step>('meal-selection');
  const [selectedMeal, setSelectedMeal] = useState<MealType | null>(null);
  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [guests, setGuests] = useState(2);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleMealSelect = (meal: MealType) => {
    setSelectedMeal(meal);
    setStep('booking-form');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    const emailSubject = `[BORDSBOKNING · ${selectedMeal}] ${date} kl ${time} – ${guests} pers – ${name}`;
    const emailMessage = [`════════════════════════════════`, `   NY BORDSBOKNING`, `   Gäller: Vanlig bordsbokning – ${selectedMeal}`, `════════════════════════════════`, '', `▸ DEL:        Bordsbokning (ordinarie servering)`, `▸ MÅLTID:     ${selectedMeal}`, `▸ DATUM:      ${date}`, `▸ TID:        ${time}`, `▸ GÄSTER:     ${guests} st`, '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   KONTAKTUPPGIFTER`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Namn:     ${name}`, `  Telefon:  ${phone}`, `  E-post:   ${email}`, '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   ÖNSKEMÅL / MEDDELANDE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  ${message || 'Inga särskilda önskemål'}`, '', `════════════════════════════════`, `Svara direkt på detta mejl för att nå gästen.`, `════════════════════════════════`].join('\n');
    try {
      const ok = await sendBokabordMail({
        subject: emailSubject,
        fromName: `Top Notch Bordsbokning – ${name}`,
        replyTo: email,
        message: emailMessage
      });
      if (!ok) throw new Error('Kunde inte skicka bokning');
      setStep('success');
    } catch (err) {
      setError('Något gick fel. Vänligen försök igen eller ring oss på 0470-52 36 03.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <AnimatePresence data-id="element-18" data-mp="urkj1hr6qkvu">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-19" data-mp="ur2fe02714dw">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} onClick={e => e.stopPropagation()} className="bg-[#F2EDE4] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-8" data-id="element-20" data-mp="18pik5lghwkte">
          {/* Header */}
          <div className="bg-white px-4 md:px-6 py-3 md:py-4 border-b border-[#7C8062]/15 flex items-center justify-between sticky top-0 z-10" data-id="element-21" data-mp="2p4wgi38pecf0">
            <div className="flex items-center gap-2 md:gap-3 min-w-0" data-id="element-22" data-mp="2yxajt0ffjr3a">
              {step === 'booking-form' && <button onClick={() => setStep('meal-selection')} className="flex items-center gap-1.5 px-3 py-2 -ml-2 text-sm font-medium text-[#7C8062] hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-lg transition-colors shrink-0" aria-label="Tillbaka" data-id="element-23" data-mp="2tyqy914r0hbg">
                  <ArrowLeft className="w-4 h-4" data-id="element-24" data-mp="2fa4ln76ia3nx" />
                  <span data-id="element-25" data-mp="2b6oom9qiy1ap">Tillbaka</span>
                </button>}
              <h2 className="text-lg md:text-xl font-serif text-[#1A1A1A] uppercase tracking-wide truncate" data-id="element-26" data-mp="3qnyjs95daqfd">
                Boka Bord
              </h2>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-[#1A1A1A]/50 hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-27" data-mp="3mm3hp2fm490j">
              <X className="w-5 h-5" data-id="element-28" data-mp="3k7ycz2po3v0g" />
            </button>
          </div>

          <div className="p-6 md:p-8" data-id="element-29" data-mp="3k9sxwh0xvn4j">
            {step === 'meal-selection' && <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-30" data-mp="3p71jysp7kbb2">
                <div className="space-y-2" data-id="element-31" data-mp="byyf329eu7tu">
                  <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-32" data-mp="zpu7a0hs351m">
                    Varmt välkommen att boka bord.
                  </h3>
                  <p className="text-[#1A1A1A]/70 text-sm" data-id="element-33" data-mp="14hlv439juwo2">
                    Notera att vi har ett par platser för drop in-gäster.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-34" data-mp="19cy0t7yg09ls">
                  {mealOptions.map(meal => <button key={meal.type} onClick={() => handleMealSelect(meal.type)} className="w-full text-left p-5 bg-white border border-[#7C8062]/20 rounded-xl hover:border-[#7C8062] hover:shadow-md transition-all group" data-id="element-35" data-mp="15bcuk5geytkk">
                      <h4 className="text-lg font-serif text-[#1A1A1A] mb-1 group-hover:text-[#7C8062] transition-colors" data-id="element-36" data-mp="qlx7p0k078a5">
                        {meal.type}
                      </h4>
                      <p className="text-sm text-[#1A1A1A]/60" data-id="element-37" data-mp="hmtkbkdt06pu">
                        {meal.description}
                      </p>
                    </button>)}
                </div>

                <div className="p-4 bg-[#7C8062]/5 rounded-xl border border-[#7C8062]/15 space-y-2" data-id="element-38" data-mp="7nuba0p4m92b">
                  <p className="text-sm font-medium text-[#1A1A1A]" data-id="element-39" data-mp="2jc8w57vdy3j4">
                    🏠 Vill du abonnera en del av eller hela lokalen?
                  </p>
                  <p className="text-xs text-[#1A1A1A]/70 leading-relaxed" data-id="element-40" data-mp="2ocdhpcb5hog7">
                    Mejla{' '}
                    <a href="mailto:bokabord@topnotchbakery.se?subject=Abonering" className="text-[#7C8062] font-semibold underline underline-offset-2 hover:text-[#6A6D53] transition-colors" data-id="element-41" data-mp="2xb8keeezkger">
                      bokabord@topnotchbakery.se
                    </a>{' '}
                    med ändamålet i ämnesraden, t.ex.{' '}
                    <span className="italic text-[#1A1A1A]/90" data-id="element-42" data-mp="2eccsxauiig4m">
                      "Abonering Frukost"
                    </span>
                    ,{' '}
                    <span className="italic text-[#1A1A1A]/90" data-id="element-43" data-mp="1htyqeibvpxbo">
                      "Abonering AW"
                    </span>{' '}
                    osv. Skriv gärna i mejlet hur många ni är, vilket datum och
                    vilka tider det gäller.
                  </p>
                </div>

                <button onClick={onClose} className="w-full py-3 text-sm font-medium text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors" data-id="element-44" data-mp="ab7nd8wkpbw3">
                  Stäng
                </button>
              </motion.div>}

            {step === 'booking-form' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-45" data-mp="y592pgu0f0wy">
                <div className="mb-6 pb-4 border-b border-[#7C8062]/15" data-id="element-46" data-mp="1gxbiyr2qof6o">
                  <h3 className="text-xl font-serif text-[#1A1A1A] mb-1" data-id="element-47" data-mp="1cwrojv9jvpo8">
                    Boka {selectedMeal}
                  </h3>
                  <p className="text-sm text-[#1A1A1A]/60" data-id="element-48" data-mp="1vm3je7zom5wk">
                    Fyll i dina uppgifter nedan för att slutföra bokningen.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-49" data-mp="1mkx0js9k8j05">
                  <div className="space-y-4" data-id="element-50" data-mp="1rj6w43z75lma">
                    <div data-id="element-51" data-mp="1rhvsq59nyfvp">
                      <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-52" data-mp="1rhdqms0bl7fv">
                        Namn *
                      </label>
                      <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="För- och efternamn" data-id="element-53" data-mp="1hlakzc69eqo6" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-54" data-mp="36y3xzgtfdede">
                      <div data-id="element-55" data-mp="3azpcf6lly0pp">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-56" data-mp="3b10acpsdge8w">
                          Telefon *
                        </label>
                        <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="070-123 45 67" data-id="element-57" data-mp="31z0va30x3k8l" />
                      </div>
                      <div data-id="element-58" data-mp="chtjdd1bai07">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-59" data-mp="chjr1issnyrc">
                          E-post *
                        </label>
                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="din@epost.se" data-id="element-60" data-mp="21yxcio2vxgxb" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-61" data-mp="1e32sh6eebhr3">
                      <div data-id="element-62" data-mp="1s2c5jx7b1vqu">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-63" data-mp="1s2kn42edhp8d">
                          Antal gäster *
                        </label>
                        <div className="relative" data-id="element-64" data-mp="33fepn28m53fr">
                          <input type="number" min="1" max="20" required value={guests} onChange={e => setGuests(parseInt(e.target.value) || 1)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-65" data-mp="33i99osnpsm8n" />
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-66" data-mp="1hf0ztbua58kl" />
                        </div>
                      </div>
                      <div data-id="element-67" data-mp="1mcaub5wj0afa">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-68" data-mp="1r5dtpunzie3e">
                          Datum *
                        </label>
                        <div className="relative" data-id="element-69" data-mp="tndmxxtury1b">
                          <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-70" data-mp="ynhwf3wcfsi1" />
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-71" data-mp="d7ls1dfg3dpp" />
                        </div>
                      </div>
                      <div data-id="element-72" data-mp="3dmqogn73xyt">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-73" data-mp="3ul7lips0zkqz">
                          Tid *
                        </label>
                        <div className="relative" data-id="element-74" data-mp="3pkkzfcikezg6">
                          <input type="time" required value={time} onChange={e => setTime(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-75" data-mp="3llc7md735bn9" />
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-76" data-mp="1ar784tn553hm" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-77" data-mp="15tx7ehuax5eh">
                      <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-78" data-mp="15u6zqc2tjonc">
                        Önskemål / Meddelande
                      </label>
                      <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full min-h-[80px] p-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm resize-y" placeholder="Allergier, barnvagn, eller andra önskemål..." data-id="element-79" data-mp="wt8y20furt7x" />
                    </div>
                  </div>

                  {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100" data-id="element-80" data-mp="3a4jzfx6nryzp">
                      {error}
                    </div>}

                  <div className="pt-4" data-id="element-81" data-mp="3k0wv30lrkmx3">
                    <Button type="submit" isLoading={isSubmitting} className="w-full py-3.5 text-base shadow-sm" data-id="element-82" data-mp="3jzv7xxrh49z0">
                      Skicka bokningsförfrågan
                    </Button>
                    <p className="text-center text-xs text-[#1A1A1A]/50 mt-3" data-id="element-83" data-mp="3p243n1vqdq4t">
                      Vi återkommer med en bekräftelse så snart vi kan.
                    </p>
                  </div>
                </form>
              </motion.div>}

            {step === 'success' && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-8 space-y-4" data-id="element-84" data-mp="3tstfcqhfj63b">
                <div className="w-16 h-16 bg-[#7C8062]/10 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-85" data-mp="ckzg6mcwc4ct">
                  <CheckCircle2 className="w-8 h-8 text-[#7C8062]" data-id="element-86" data-mp="gix65rehquh9" />
                </div>
                <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-87" data-mp="li9s89xjfxis">
                  Tack för din förfrågan!
                </h3>
                <p className="text-[#1A1A1A]/70 max-w-sm mx-auto" data-id="element-88" data-mp="v9w84jktmkrv">
                  Vi har tagit emot din bokningsförfrågan för {selectedMeal} den{' '}
                  {date} kl {time}. Vi återkommer till dig via e-post eller
                  telefon med en bekräftelse.
                </p>
                <div className="pt-6" data-id="element-89" data-mp="2osmt7h7ev9wb">
                  <Button onClick={onClose} className="w-full sm:w-auto px-8" data-id="element-90" data-mp="2oto4nmsc9yek">
                    Stäng
                  </Button>
                </div>
              </motion.div>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}